// Paramètres d'URL des parcours d'accès (A14) et de l'administration des comptes : validés par le routeur, jamais
// interprétés comme du HTML.
import { parseListSearch, type ListSearch } from '@/lib/list-query';

// Le routeur convertit les valeurs numériques de l'URL (« ?code=654321 », « ?identifiant=70123456 ») en nombres.
const text = (value: unknown, max = 255): string | undefined => {
  const raw = typeof value === 'number' && Number.isFinite(value) ? String(value) : value;
  return typeof raw === 'string' && raw !== '' ? raw.slice(0, max) : undefined;
};

export interface LoginSearch {
  redirect?: string;
  identifiant?: string;
  raison?: 'mot-de-passe-modifie' | 'compte-active' | 'deconnecte';
}

const REASONS = ['mot-de-passe-modifie', 'compte-active', 'deconnecte'] as const;

export function parseLoginSearch(raw: Record<string, unknown>): LoginSearch {
  const reason = text(raw.raison, 40);
  return {
    redirect: text(raw.redirect, 500),
    identifiant: text(raw.identifiant),
    raison: REASONS.find((r) => r === reason),
  };
}

export interface IdentifierSearch {
  identifiant?: string;
}

export function parseIdentifierSearch(raw: Record<string, unknown>): IdentifierSearch {
  return { identifiant: text(raw.identifiant) };
}

export interface ResetSearch {
  identifiant?: string;
  /** Code prérempli par le lien de l'email (ACC-P04 « lien reçu »). */
  code?: string;
  /** Canal demandé dans ACC-P03 (lot F17c) : « sms » → code reçu par SMS ou par email (F17-18). */
  canal?: 'sms';
}

export function parseResetSearch(raw: Record<string, unknown>): ResetSearch {
  const code = text(raw.code, 12)?.replace(/\D/g, '').slice(0, 6);
  return { identifiant: text(raw.identifiant), code: code || undefined, canal: raw.canal === 'sms' ? 'sms' : undefined };
}

/** Refus de connexion (codes de l'API) → vue de ACC-P06 (`/compte/$state`). */
export const REFUSAL_STATES = {
  ACCOUNT_BLOCKED: 'bloque',
  ACCOUNT_DISABLED: 'desactive',
  ACCOUNT_NOT_ACTIVATED: 'non-active',
} as const;

export function refusalState(code: string | undefined): (typeof REFUSAL_STATES)[keyof typeof REFUSAL_STATES] | undefined {
  return code !== undefined && code in REFUSAL_STATES ? REFUSAL_STATES[code as keyof typeof REFUSAL_STATES] : undefined;
}

/** ACC-P09 : onglet actif et liste de l'onglet Activité. */
export interface UserDetailSearch extends ListSearch {
  onglet?: string;
}

export function parseUserDetailSearch(raw: Record<string, unknown>): UserDetailSearch {
  const onglet = text(raw.onglet, 20);
  return { ...parseListSearch(raw), ...(onglet ? { onglet } : {}) };
}

/** ROL-P03 : rôle (nom technique) ou utilisateur (UUID) prévisualisé. */
export interface ViewAsSearch {
  role?: string;
  user?: string;
}

export function parseViewAsSearch(raw: Record<string, unknown>): ViewAsSearch {
  const role = text(raw.role, 60);
  const user = text(raw.user, 36);
  return { ...(role ? { role } : {}), ...(user ? { user } : {}) };
}

/** AUD-P02 : période (AAAA-MM-JJ). */
export interface PeriodSearch {
  from?: string;
  to?: string;
}

const isoDate = (value: unknown): string | undefined => (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined);

export function parsePeriodSearch(raw: Record<string, unknown>): PeriodSearch {
  const from = isoDate(raw.from);
  const to = isoDate(raw.to);
  return { ...(from ? { from } : {}), ...(to ? { to } : {}) };
}
